/**
 * Api_Endpoints.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for every backend URL used in SK Catalog.
 *
 * Two backend services:
 *   PLATFORM_API  → skplatform-api   (auth & identity — login / refresh / me;
 *                   issues the httpOnly cookies directly)
 *   CATALOG_API   → sk-catalog-api   (items, bundles, media, search, etc.)
 *
 * Auth/identity call skplatform-api DIRECTLY (:3013 in local dev). An earlier
 * design routed them through the sk-backbone hub (:5001), but the hub only
 * proxied a byte-for-byte copy of the platform's contract — so the indirection
 * added a hard dependency on the backbone being up, for no benefit, and a
 * backbone outage broke session boot entirely. Calling the platform directly
 * removes that dependency (matches SK-Upsell). The Platform_*Url names below
 * describe the platform's auth contract, unchanged.
 *
 * Every path below is verified 1:1 against the live backend route tables
 * (sk-backbone-api src/routes/auth.ts → skplatform-api
 *  src/modules/identity/routes/*, and sk-catalog-api src/routes/*).
 *
 * Rules:
 *   - Zero hardcoded URLs anywhere else in the codebase.
 *   - To switch environments, update ONLY the base URLs below.
 * ──────────────────────────────────────────────────────────────────────────
 */

// ── PLATFORM API base (auth, identity — called directly) ──────────────────
// Injected at build time via webpack DefinePlugin (PLATFORM_API_URL env var).
// Local dev: the platform (skplatform-api) runs on :3013.
// Stage/Prod: Cloud Build passes --build-arg PLATFORM_API_URL=https://...
export const PLATFORM_API = process.env.PLATFORM_API_URL || 'http://localhost:3013';

// ── CATALOG API base (items, bundles, search, media, etc.) ────────────────
// Injected at build time via webpack DefinePlugin (CATALOG_API_URL env var).
export const CATALOG_API = process.env.CATALOG_API_URL || 'http://localhost:3001';

// ── Versioned prefixes ─────────────────────────────────────────────────────
const P = `${PLATFORM_API}/v1`;    // Auth/identity — skplatform-api v1 (direct)
const C = `${CATALOG_API}/api/v1`; // Catalog  v1

/* ═══════════════════════════════════════════════════════════════════════════
   PLATFORM API — Auth & Identity  (httpOnly cookie session)
═══════════════════════════════════════════════════════════════════════════ */

export const Platform_Signup_Url = `${P}/auth/signup`;             // POST { first_name, last_name, business_name, country, email, password, country_code, phone_number, product }
export const Platform_Login_Url = `${P}/auth/login`;              // POST { email, password } → user/org OR { kind:'mfa_required', mfa_token }
export const Platform_Login_Mfa_Url = `${P}/auth/login/mfa`;          // POST { mfa_token, code }
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`;            // POST — silent rotation (httpOnly cookie)
export const Platform_Logout_Url = `${P}/auth/logout`;             // POST — clears session cookies (204)
export const Platform_Me_Url = `${P}/me`;                      // GET  — identity probe (200 = logged in, 401 = not)
export const Platform_Forgot_Password_Url = `${P}/auth/forgot-password`;    // POST { email, product } (204)
export const Platform_Reset_Password_Url = `${P}/auth/reset-password`;     // POST { token, new_password } (204)
export const Platform_Verify_Email_Url = `${P}/auth/verify-email`;       // POST { token } → { verified, activated_org_ids }
export const Platform_Resend_Verification_Url = `${P}/auth/resend-verification`; // POST { product } — AUTHENTICATED (session cookie); resends to the logged-in user only (204)
export const Platform_Switch_Org_Url = `${P}/auth/switch-org`;         // POST { org_id }
// Platform owns the branch. Catalog mirrors one; it never invents a branch.
export const Platform_Locations_Url = `${P}/locations`;                // GET → { data: [{ id: loc_<ULID>, name, code, city, timezone, … }] }

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Item Types (schema engine)
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Analytics_Overview_Url = `${C}/analytics/overview`;         // GET — dashboard analytics
export const Catalog_FieldTypes_Url = `${C}/field-types`;                        // GET — DB-backed field-type catalog (labels, tiers, availability)
export const Catalog_ItemTypes_Url = `${C}/item-types`;                          // GET | POST
export const Catalog_ItemType_Url = (id: string) => `${C}/item-types/${id}`;    // GET | PATCH
export const Catalog_ItemType_Image_Url = `${C}/item-types/image-upload`;        // POST — presign type image
export const Catalog_ItemType_Fields_Url = (id: string) => `${C}/item-types/${id}/fields`; // GET | POST
export const Catalog_ItemType_Field_Url = (id: string, fieldKey: string) => `${C}/item-types/${id}/fields/${fieldKey}`; // DELETE

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Items
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Items_Url = `${C}/items`;                                  // GET (cursor-paginated) | POST
export const Catalog_Item_Url = (id: string) => `${C}/items/${id}`;            // GET | PATCH (no DELETE — deactivate only)
export const Catalog_Item_Publish_Url = (id: string) => `${C}/items/${id}/publish`;    // POST — DRAFT → PUBLISHED
export const Catalog_Item_Deactivate_Url = (id: string) => `${C}/items/${id}/deactivate`; // POST — → DEACTIVATED
export const Catalog_Item_Resolve_Url = (id: string) => `${C}/items/${id}/resolve`;    // GET  — locale-aware resolved view
export const Catalog_Item_Versions_Url = (id: string) => `${C}/items/${id}/versions`;   // GET  — audit history
export const Catalog_Item_Export_Url = (id: string) => `${C}/items/${id}/export`;     // GET  — DPDP data export
export const Catalog_Item_Variants_Url = (id: string) => `${C}/items/${id}/variants`;   // GET  — list variants

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Bundles (components) + Price propagation
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Item_Components_Url = (id: string) => `${C}/items/${id}/components`;                       // GET | POST { child_id, qty, relation_type }
export const Catalog_Item_Component_Url = (id: string, childId: string) => `${C}/items/${id}/components/${childId}`; // PATCH | DELETE
export const Catalog_Item_PriceImpact_Url = (id: string) => `${C}/items/${id}/component-price-impact`;          // GET  — read-only preview
export const Catalog_Item_Price_Url = (id: string) => `${C}/items/${id}/price`;                           // POST — commit component + ancestor price updates
export const Catalog_Item_RelinkPrice_Url = (id: string) => `${C}/items/${id}/relink-price`;                    // POST — recompute / pin bundle price

// SKCATALOG-CC-002 CR-5 / CAT-D2 — effective-dated base price. GET returns the
// price in force now (or at `?at=`); PUT closes the open row and opens a new one
// (never an in-place overwrite); /history returns the dated ladder, closed rows
// included — that is the point of the table.
export const Catalog_Item_BasePrice_Url = (id: string) => `${C}/items/${id}/base-price`;                        // GET ?location=&at= | PUT { priceMinor, currencyCode?, locationId? }
export const Catalog_Item_BasePrice_History_Url = (id: string) => `${C}/items/${id}/base-price/history`;        // GET ?location=&allScopes=1
export const Catalog_AttentionQueue_Url = `${C}/attention-queue`;                                             // GET  — items flagged for attention

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Media (async upload pipeline, field-scoped)
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Media_Url = (id: string, fieldKey: string) => `${C}/items/${id}/fields/${fieldKey}/media`;          // POST initiate (presigned PUT) | GET status
export const Catalog_Media_Complete_Url = (id: string, fieldKey: string) => `${C}/items/${id}/fields/${fieldKey}/media/complete`; // POST finalize → PROCESSING
export const Catalog_Media_Clear_Url = (id: string, fieldKey: string) => `${C}/items/${id}/fields/${fieldKey}/media/clear`;    // POST clear failed upload

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Search  (pgvector + full-text)
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Search_Url = `${C}/search`; // GET ?q=&locale=&limit=

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Public Catalog (published items)
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Public_Items_Url = `${C}/public/items`;                       // GET
export const Catalog_Public_Item_Url = (id: string) => `${C}/public/items/${id}`; // GET

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Import (bulk upsert)
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Import_Url = `${C}/import`; // POST — bulk upsert, returns 207 Multi-Status

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Locations
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Locations_Url = `${C}/locations`;                        // GET | POST
export const Catalog_Location_Url = (id: string) => `${C}/locations/${id}`;  // GET | PATCH

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Taxonomy (Categories, Collections, Tags)
═══════════════════════════════════════════════════════════════════════════ */

// Categories
export const Catalog_Categories_Url = `${C}/categories`;                              // GET | POST
export const Catalog_Category_Url = (id: string) => `${C}/categories/${id}`;        // GET | PATCH | DELETE
export const Catalog_Item_Categories_Url = (id: string) => `${C}/items/${id}/categories`; // PUT — replace item's categories

// Collections
export const Catalog_Collections_Url = `${C}/collections`;                                 // GET | POST
export const Catalog_Collection_Url = (id: string) => `${C}/collections/${id}`;           // GET | PATCH | DELETE
export const Catalog_Collection_Items_Url = (id: string) => `${C}/collections/${id}/items`;     // POST — add items
export const Catalog_Collection_Item_Url = (id: string, itemId: string) => `${C}/collections/${id}/items/${itemId}`; // PATCH | DELETE
export const Catalog_Collection_Items_Order_Url = (id: string) => `${C}/collections/${id}/items/order`; // PUT — batch reorder

// Tags
export const Catalog_Tags_Url = `${C}/tags`;                              // GET | POST
export const Catalog_Tag_Url = (id: string) => `${C}/tags/${id}`;        // DELETE
export const Catalog_Item_Tags_Url = (id: string) => `${C}/items/${id}/tags`;  // PUT — replace item's tags

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Webhooks
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Webhooks_Url = `${C}/webhooks`;                                  // GET | POST
export const Catalog_Webhook_Url = (id: string) => `${C}/webhooks/${id}`;            // GET | PATCH | DELETE
export const Catalog_Webhook_RotateSecret_Url = (id: string) => `${C}/webhooks/${id}/rotate-secret`; // POST
export const Catalog_Webhook_Deliveries_Url = (id: string) => `${C}/webhooks/${id}/deliveries`;   // GET — delivery history
export const Catalog_Webhook_DeliveryRetry_Url = (id: string, deliveryId: string) => `${C}/webhooks/${id}/deliveries/${deliveryId}/retry`; // POST

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — API Keys
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_ApiKeys_Url = `${C}/api-keys`;                                   // GET | POST
export const Catalog_ApiKey_Url = (id: string) => `${C}/api-keys/${id}`;             // GET | DELETE
export const Catalog_ApiKey_Rotate_Url = (id: string) => `${C}/api-keys/${id}/rotate`;      // POST — rotate key

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — AI Suggestions
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Item_Suggestions_Url = (id: string) => `${C}/items/${id}/suggestions`;   // POST — AI authoring
export const Catalog_Item_Suggestions_Generate_Url = (id: string) => `${C}/items/${id}/suggestions/generate`; // POST — real AI (Claude)
export const Catalog_Suggestions_Url = `${C}/suggestions`;                              // GET  — review queue
export const Catalog_Suggestion_Url = (id: string) => `${C}/suggestions/${id}`;        // GET
export const Catalog_Suggestion_Accept_Url = (id: string) => `${C}/suggestions/${id}/accept`; // POST
export const Catalog_Suggestion_Reject_Url = (id: string) => `${C}/suggestions/${id}/reject`; // POST

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — MCP (Model Context Protocol server) + Health
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_MCP_Url = `${C}/mcp`;                       // POST — JSON-RPC
export const Catalog_Health_Url = `${CATALOG_API}/api/v1/health`;   // GET — unauthenticated health check

/* ═══════════════════════════════════════════════════════════════════════════
   CATALOG API — Metering (per-plan usage limits via @softknack/meter)
═══════════════════════════════════════════════════════════════════════════ */

export const Catalog_Meter_Usage_Url = `${C}/meter/usage`;       // GET — current org's usage vs plan caps
